// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { CommunicationIdentifierKind } from '@azure/communication-common';
import { CallState, DeviceManagerState } from '@internal/calling-stateful-client';
import { ChatThreadClientState } from '@internal/chat-stateful-client';
import { CallAdapter, CallAdapterState, CallCompositePage } from '../../CallComposite';
/* @conditional-compile-remove(video-background-effects) */
import { VideoBackgroundImage, VideoBackgroundEffect } from '../../CallComposite';
/* @conditional-compile-remove(video-background-effects) */
import { VideoBackgroundEffectsDependency } from '@internal/calling-component-bindings';
import { ChatAdapter, ChatAdapterState } from '../../ChatComposite';
import { AdapterErrors } from '../../common/adapters';
/**
 * UI state pertaining to the {@link CallWithChatComposite}.
 *
 * @public
 */
export interface CallWithChatAdapterUiState {
  /**
   * Microphone state before a call has joined.
   *
   * @public
   */
  isLocalPreviewMicrophoneEnabled: boolean;
  /**
   * Current page of the Composite.
   *
   * @public
   */
  page: CallCompositePage;
}

/**
 * State from the backend services that drives {@link CallWithChatComposite}.
 *
 * @public
 */
export interface CallWithChatClientState {
  /** ID of the call participant using this CallWithChatAdapter. */
  userId: CommunicationIdentifierKind;
  /** Display name of the participant using this CallWithChatAdapter. */
  displayName: string | undefined;
  /** State of the current call. */
  call?: CallState;
  /** State of the current chat. */
  chat?: ChatThreadClientState;
  /** Latest call error encountered for each operation performed via the adapter. */
  latestCallErrors: AdapterErrors;
  /** Latest chat error encountered for each operation performed via the adapter. */
  latestChatErrors: AdapterErrors;
  /** State of available and currently selected devices */
  devices: DeviceManagerState;
  /** State of whether the active call is a Teams interop call */
  isTeamsCall: boolean;
  /* @conditional-compile-remove(video-background-effects) */
  /** Default set of background images for background replacement effect */
  videoBackgroundImages?: VideoBackgroundImage[];
  /* @conditional-compile-remove(video-background-effects) */
  /** Dependency to be injected for video background effects */
  onResolveVideoEffectDependency?: () => Promise<VideoBackgroundEffectsDependency>;
  /* @conditional-compile-remove(video-background-effects) */
  /** State to track the selected video background effect */
  selectedVideoBackgroundEffect?: VideoBackgroundEffect;
}

/**
 * CallWithChat State is a combination of Stateful Chat and Stateful Calling clients with some
 * state specific to the CallWithChat Composite only.
 *
 * @public
 */
export interface CallWithChatAdapterState extends CallWithChatAdapterUiState, CallWithChatClientState {}

/**
 * @private
 */
export function callWithChatAdapterStateFromBackingStates(callAdapter: CallAdapter, chatAdapter: ChatAdapter): CallWithChatAdapterState {
  const callAdapterState = callAdapter.getState();
  const chatAdapterState = chatAdapter.getState();
  return {
    call: callAdapterState.call,
    chat: chatAdapterState.thread,
    userId: callAdapterState.userId,
    page: callAdapterState.page,
    displayName: callAdapterState.displayName,
    devices: callAdapterState.devices,
    isLocalPreviewMicrophoneEnabled: callAdapterState.isLocalPreviewMicrophoneEnabled,
    isTeamsCall: callAdapterState.isTeamsCall,
    latestCallErrors: callAdapterState.latestErrors,
    latestChatErrors: chatAdapterState.latestErrors,
    /* @conditional-compile-remove(video-background-effects) */
    videoBackgroundImages: callAdapterState.videoBackgroundImages,
    /* @conditional-compile-remove(video-background-effects) */
    onResolveVideoEffectDependency: callAdapterState.onResolveVideoEffectDependency,
    /* @conditional-compile-remove(video-background-effects) */
    selectedVideoBackgroundEffect: callAdapterState.selectedVideoBackgroundEffect
  };
}

/**
 * @private
 */
export function mergeChatAdapterStateIntoCallWithChatAdapterState(existingCallWithChatAdapterState: CallWithChatAdapterState, chatAdapterState: ChatAdapterState): CallWithChatAdapterState {
  return {
    ...existingCallWithChatAdapterState,
    chat: chatAdapterState.thread,
    latestChatErrors: chatAdapterState.latestErrors
  };
}

/**
 * @private
 */
export function mergeCallAdapterStateIntoCallWithChatAdapterState(existingCallWithChatAdapterState: CallWithChatAdapterState, callAdapterState: CallAdapterState): CallWithChatAdapterState {
  return {
    ...existingCallWithChatAdapterState,
    userId: callAdapterState.userId,
    page: callAdapterState.page,
    displayName: callAdapterState.displayName,
    devices: callAdapterState.devices,
    call: callAdapterState.call,
    isLocalPreviewMicrophoneEnabled: callAdapterState.isLocalPreviewMicrophoneEnabled,
    isTeamsCall: callAdapterState.isTeamsCall,
    latestCallErrors: callAdapterState.latestErrors,
    /* @conditional-compile-remove(video-background-effects) */
    videoBackgroundImages: callAdapterState.videoBackgroundImages,
    /* @conditional-compile-remove(video-background-effects) */
    onResolveVideoEffectDependency: callAdapterState.onResolveVideoEffectDependency,
    /* @conditional-compile-remove(video-background-effects) */
    selectedVideoBackgroundEffect: callAdapterState.selectedVideoBackgroundEffect
  };
}"../../../../../calling-stateful-client/src""../../../../../chat-stateful-client/src""../../../../../calling-component-bindings/src"